// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { switchMap } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';
import { Colours } from '../../../base-styles/colours';
import { YearByYearDataSeries } from '../../../feature/dashboard/services/dashboard.service/helpers/year-by-year-data-series';
import { UI_STRINGS } from '../../../functions/language/uistrings';
import { getBoardYearsFromTimeline } from '../../../functions/timeline/get-board-years-from-timeline';
import { getTransitionYearsFromTimeline } from '../../../functions/timeline/get-transition-years-from-timeline';
import { BoardSettings } from '../../../model/board-settings';
import { ClientFeatureFlagName } from '../../../model/client-model/client-feature-flag';
import { NodeType } from '../../../model/client-model/properties/node-type';
import { UnaddressedEmissionsDisplay } from '../../../model/client-model/settings/board-settings/unaddressed-emissions-display';
import { Dict } from '../../../model/dict';
import { Language } from '../../../model/enums/language';
import { YearByYearDataService } from '../../../modules/diagram/components/year-by-year-diagram/services/year-by-year-diagram-data.service';
import { Series } from '../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series';
import { SeriesData } from '../../../modules/diagram/compound-renderers/chart-renderer/chart/series/series-data';
import { ABATEMENT_SERIES_ID, UNADDRESSED_EMISSIONS_ID } from '../../../public/dashboard/shared/dashboard.constants';
import { ClientBoardContainer } from '../../../public/dashboard/shared/model/client-board-container';
import { DashboardColours } from '../../../public/dashboard/shared/model/dashboard-colours';
import { DashboardNodeV3 } from '../../../public/dashboard/v3/dashboard-node-v3';
import { DashboardV3 } from '../../../public/dashboard/v3/dashboard-v3';
import { dashboardStyles } from '../../../public/dashboard/v4/shared/dashboard.styles';
import { ModelService } from '../../../services/backend-service/model.service';
import { BackendFeatureFlagsService } from '../../../services/backend/backend-feature-flags.service';
import { ContainedBoard } from '../../../services/backend/backend-published-board.types';
import { PublishedBoardsFacadeService } from '../../../services/store/published-boards/published-boards-facade.service';
import { TranslationService } from '../../../services/translation-service';
import { StrippedDashboardNodeV3, StrippedDashboardV3 } from './self-contained-dashboard-v3.types';

@Injectable({
  providedIn: 'root',
})
export class SelfContainedDashboardResolverV3 implements Resolve<DashboardV3> {
  constructor(
    private _featureFlagsService: BackendFeatureFlagsService,
    private _modelService: ModelService,
    private _publishedBoardsFacade: PublishedBoardsFacadeService,
    private _translationService: TranslationService,
    private _yearByYearDataService: YearByYearDataService
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<DashboardV3> {
    return this._resolveBoardAndLoadLanguage(route.params.boardId || route.data.boardId);
  }

  /**
   * creates a series for the unaddressed emissions in the board
   * this is a temporary function, will be used until we have improved the calculation for targeted emissions and unaddressed emissions.
   * Copy of same function in dashboard.service.ts
   */
  private _createUnaddressedEmissionSeries(
    lang: Language,
    boardYears: number[],
    startYear: number,
    unaddressedEmissions: number
  ): Series {
    const data: SeriesData[] = boardYears.map((year) => {
      return {
        label: year + '',
        value: year < startYear ? undefined : unaddressedEmissions,
      };
    });
    return {
      data,
      label: this._translationService.translate(lang, UI_STRINGS.DASHBOARD.UNADDRESSED_EMISSIONS_LABEL),
      id: UNADDRESSED_EMISSIONS_ID,
    };
  }

  private _createYearData = (abatementData: number[], bauData: number[], highlightedYearIndex: number) => {
    return {
      highlighted: this._createYearValues(abatementData, bauData, highlightedYearIndex),
      end: this._createYearValues(abatementData, bauData, abatementData.length - 1),
      start: this._createYearValues(abatementData, bauData, 0),
    };
  };

  private _createYearValues(abatementData: number[], emissionBauData: number[], yearIndex: number) {
    const abatement = abatementData[yearIndex] ?? 0;
    const emissionBau = emissionBauData[yearIndex] ?? 0;
    return {
      abatement,
      emissionBau,
      remainingEmissions: emissionBau - abatement,
    };
  }

  private _reconstructDashboard(board: ContainedBoard, actions, strippedDashboard: StrippedDashboardV3): DashboardV3 {
    const timeline = (board.boardSettings as BoardSettings).timeline;
    const years = getTransitionYearsFromTimeline(timeline);
    const boardYears = getBoardYearsFromTimeline(timeline);
    const highlightedYear = timeline.scenarioStartYear;
    const highlightedYearIndex: number = boardYears.indexOf(highlightedYear);
    const colourDict = Object.entries(strippedDashboard.nodes).reduce<Dict<DashboardColours>>(
      (obj, [id, node]) => {
        obj[id] = node.colours;
        return obj;
      },
      { [board.id]: strippedDashboard.colours }
    );

    const rebuiltNodes = Object.entries(strippedDashboard.nodes).reduce<Dict<DashboardNodeV3>>((obj, [id, node]) => {
      if (node.nodeProperties.type !== NodeType.SupplyTarget) {
        const reconstructedSeries = node.childNodes.length
          ? this._reconstructSeries(strippedDashboard, board, node)
          : (node.nodeData as YearByYearDataSeries);
        const isPanorama = this._featureFlagsService.isEnabled(ClientFeatureFlagName.Panorama);
        const diagramData = this._yearByYearDataService.create(
          node,
          reconstructedSeries,
          colourDict,
          dashboardStyles.colours.dashboardContentPaneBackgroundColour,
          highlightedYearIndex,
          isPanorama
        );
        diagramData.highlightColours = {
          ...diagramData.highlightColours,
          [board.id]: strippedDashboard.colours.main,
        };
        const yearData = this._createYearData(
          reconstructedSeries.netCarbonAbatement.data.map((d) => d.value),
          reconstructedSeries.emissionBau.data.map((d) => d.value),
          highlightedYearIndex
        );
        obj[id] = { ...node, nodeData: reconstructedSeries, diagramData, yearData };
      }
      return obj;
    }, {});

    const rebuiltIndicators = Object.values(rebuiltNodes)
      .filter((n) => n.indicators.length !== 0)
      .reduce((indicators, node) => {
        const indicatorId = node.indicators[0];
        indicators[indicatorId] = {
          id: indicatorId,
          indicatorProperties: {
            description: node.description,
            diff: node.diff,
            unknownDiff: node.unknownDiff,
            potentialDescription: node.potentialDescription,
            outcomeDescription: node.outcomeDescription,
            assessmentDescription: node.assessmentDescription,
            goalDescription: node.goalDescription,
          },
        };
        return indicators;
      }, {});
    const boardData = this._reconstructSeries(strippedDashboard, board, strippedDashboard);

    if (
      strippedDashboard.unaddressedEmissions > 0 &&
      (board.boardSettings as BoardSettings).unaddressedEmissionsDisplay ===
        UnaddressedEmissionsDisplay.TotalBarAndDiagrams
    ) {
      //This is done outside of the series builder since it is temporary and it will only exist on the board.

      const unaddressedSeries = this._createUnaddressedEmissionSeries(
        (board.boardSettings as BoardSettings).internationalisation.defaultLanguage,
        boardYears,
        years?.[0] ?? highlightedYear,
        strippedDashboard.unaddressedEmissions
      );
      if (boardData.childRemainingEmissions) {
        boardData.childRemainingEmissions = [unaddressedSeries, ...boardData.childRemainingEmissions];
      }
      if (boardData.childRemainingEmissionsAndAbatement) {
        boardData.childRemainingEmissionsAndAbatement = [
          unaddressedSeries,
          ...boardData.childRemainingEmissionsAndAbatement,
        ];
      }
      if (boardData.leafNodeRemainingEmissions) {
        boardData.leafNodeRemainingEmissions = [unaddressedSeries, ...boardData.leafNodeRemainingEmissions];
      }
      colourDict[unaddressedSeries.id] = {
        main: Colours.unaddressed.main,
        dark: Colours.unaddressed.dark,
        light: Colours.unaddressed.light,
      };
    }

    return {
      ...strippedDashboard,
      actions,
      boardSettings: board.boardSettings as BoardSettings,
      childNodes: board.childNodes,
      supplyChildNodes: board.childNodes,
      title: board.title,
      _etag: null,
      _ts: null,
      id: board.id,
      nodes: rebuiltNodes,
      indicators: rebuiltIndicators,
      nodeData: boardData,
      diagramData: this._yearByYearDataService.create(
        { ...(strippedDashboard as any as ClientBoardContainer), id: board.id },
        boardData,
        colourDict,
        dashboardStyles.colours.dashboardContentPaneBackgroundColour,
        highlightedYearIndex
      ),
      yearData: this._createYearData(
        boardData.netCarbonAbatement.data.map((d) => d.value),
        boardData.emissionBau.data.map((d) => d.value),
        highlightedYearIndex
      ),
      years: boardYears,
    };
  }

  private _reconstructSeries(
    strippedDashboard: StrippedDashboardV3,
    board: ContainedBoard,
    node: StrippedDashboardNodeV3 | StrippedDashboardV3
  ): YearByYearDataSeries {
    const children = (node as StrippedDashboardNodeV3)?.childNodes
      ? (node as StrippedDashboardNodeV3).childNodes.map((c) => strippedDashboard.nodes[c])
      : board.childNodes.map((c) => strippedDashboard.nodes[c]);
    const leafNodeData = node.leafNodes.map((id) => strippedDashboard.nodes[id].nodeData).reverse();
    const abatementSeriesForRemainingEmissions = { ...node.nodeData.netCarbonAbatement, id: ABATEMENT_SERIES_ID };
    const dataSeries = {
      ...node.nodeData,
      remainingEmissionsAndAbatement: [node.nodeData.remainingEmissions, abatementSeriesForRemainingEmissions],
      remainingEmissionsAndChildAbatement: [node.nodeData.remainingEmissions, abatementSeriesForRemainingEmissions],
      remainingEmissionsAndLeafNodeAbatement: [node.nodeData.remainingEmissions, abatementSeriesForRemainingEmissions],
    };
    const leafNodeNetCarbonAbatement = leafNodeData.map((data) => data.netCarbonAbatement);
    const leafNodeRemainingEmissions = leafNodeData.map((data) => data.remainingEmissions);
    const childData = children
      .reverse()
      .map((c) => (c.childNodes.length ? this._reconstructSeries(strippedDashboard, board, c) : c.nodeData));
    const childNetCarbonAbatement = childData.length ? childData.map((data) => data.netCarbonAbatement) : undefined;
    const childRemainingEmissions = childData.length ? childData.map((data) => data.remainingEmissions) : undefined;
    let childHistoricalEmissions;
    if (childData.every((c) => !c.inheritedHistoricalEmissions)) {
      childHistoricalEmissions = childData.map((c) => c.historicalEmissions);
    }
    return {
      ...dataSeries,
      historicalEmissions: dataSeries.historicalEmissions,
      childHistoricalEmissions,
      childNetCarbonAbatement,
      childRemainingEmissions,
      childRemainingEmissionsAndAbatement: [
        ...childRemainingEmissions,
        { ...dataSeries.netCarbonAbatement, id: ABATEMENT_SERIES_ID },
      ],
      leafNodeRemainingEmissions,
      remainingEmissionsAndChildAbatement: [dataSeries.remainingEmissions, ...childNetCarbonAbatement],
      remainingEmissionsAndLeafNodeAbatement: [dataSeries.remainingEmissions, ...leafNodeNetCarbonAbatement],
    };
  }

  private _resolveBoardAndLoadLanguage(boardId: string): Observable<DashboardV3> {
    return this._publishedBoardsFacade
      .getPublishedBoardData$<'V3'>(boardId)
      .pipe(map((boardData) => this._modelService.v3ToStrippedDashboard(boardData)))
      .pipe(
        switchMap((board) => {
          // Not used currently since a11y/cookie use user language, but can be used when multi-language boards are added:
          // if (this._languageService.boardLanguage) {
          //   this._languageService.boardLanguage.next(dashboard.boardSettings.internationalisation.defaultLanguage);
          //
          return this._translationService
            .loadLanguageIfNeeded(board.board.boardSettings.internationalisation.defaultLanguage)
            .pipe(map(() => board));
        })
      )
      .pipe(map((board) => this._reconstructDashboard(board.board, board.actions, board.dashboard)));
  }
}
